import { CloudDownloadOutlined, LineChartOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { useDownload } from '@web/react-common'
import { Button, Typography } from 'antd'
import React from 'react'

import { useGetStatisticsQuery } from '@/generated/graphql'

import s from '../kpi.module.less'
import { compensateMonthData } from './statistics.util'

export const StatisticsPostingRate: React.FC<{
  range: {
    year?: number
    month?: number
    day?: number
    week?: number
  }
}> = ({ range }) => {
  const barDesc = '月发帖率'
  const title = '月发帖率'
  const divId = 'postingRateChartDiv'
  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetStatisticsQuery({
    skip: !range.year && !range.month && !range.day,
    variables: {
      withPostingRate: true,
      input: {
        year: range.year,
      },
    },
  })

  const statistics = compensateMonthData(range.year!, data?.statistics.postingRate || [])

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        appendPadding: [0, 25, 0, 0],
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (statistics && chart) {
      chart.clear()
      chart.scale({
        value: {
          alias: barDesc,
          sync: true,
          nice: true,
        },
      })

      chart.axis('yearMonth', {
        tickLine: {
          alignTick: true,
        },
      })

      chart.tooltip({
        showCrosshairs: true,
      })

      const view = chart.createView()
      view.data(statistics)
      view.line().position('yearMonth*value')
      view.point().position('yearMonth*value')

      chart.render()
    }
  }, [chart, statistics])

  const file = [['日期', title]].concat(
    statistics.length > 0
      ? statistics.map((value) => [value.date || value.yearMonth || value.year || '', value.value + ''])
      : [['', '']]
  )

  const fileName = `Tale${title} - ${range.year}.csv`

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <LineChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Button icon={<CloudDownloadOutlined />} type='link' onClick={() => useDownload(file, fileName)}>
          下载
        </Button>
      </div>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      {header(title)}
      <div className={s.chartBox}>
        <div id={divId} className={s.chart} ref={ref} />
      </div>
    </div>
  )
}
